import { AppGlueCoreInit } from "../../../../../AppGlueCoreInit";
import { DataDefinition } from "../../../../../rule-engine-core/Common/Data/DataDefinition";
import { BooleanDataDefinition } from "../../../../../rule-engine-core/Common/Data/Definitions/BooleanDataDefinition";
import { NumberDataDefinition } from "../../../../../rule-engine-core/Common/Data/Definitions/NumberDataDefinition";
import { StringDataDefinition } from "../../../../../rule-engine-core/Common/Data/Definitions/StringDataDefinition";
import { ExecutionData } from "../../../../../rule-engine-core/Common/Data/ExecutionData/ExecutionData";
import { ExpressionConfiguration } from "../../../../../rule-engine-core/Expressions/ExpressionConfiguration";
import { ExpressionRule } from "../../../../../rule-engine-core/Expressions/ExpressionRule";
import { GreaterThanExpression } from "../../../../../rule-engine-core/Expressions/Expressions/Logic/GreaterThan";
import { GreaterThanEqualToExpression } from "../../../../../rule-engine-core/Expressions/Expressions/Logic/GreaterThanEqualTo";
import { IsFalseExpression } from "../../../../../rule-engine-core/Expressions/Expressions/Logic/IsFalse";
import { IsTrueExpression } from "../../../../../rule-engine-core/Expressions/Expressions/Logic/IsTrue";
import { LessThanExpression } from "../../../../../rule-engine-core/Expressions/Expressions/Logic/LessThanExpression";
import { LessThanEqualToExpression } from "../../../../../rule-engine-core/Expressions/Expressions/Logic/LessThanEqualToExpression";
import { StringEqualExpression } from "../../../../../rule-engine-core/Expressions/Expressions/String/StringEqualExpression";
import { StringListInExpression } from "../../../../../rule-engine-core/Expressions/Expressions/String/StringListInExpression";
import { RuleTableConfiguration } from "../../../../../rule-engine-core/Rule/Table/Rule/RuleTableConfiguration";
import { RuleTableColumn } from "../../../../../rule-engine-core/Rule/Table/Rule/Structure/RuleTableRuleColumn";
import { RuleTableRow } from "../../../../../rule-engine-core/Rule/Table/Rule/Structure/RuleTableRow";
import type { DemoOutputQueue, DemoRuleStatus } from "../../../domain/ruleTypes";
import type { SourcePriorityRule } from "../../../domain/types";
import { SOURCE_PRIORITY_RULES } from "../../../data/generatorConfig";
import { createRuleSummaryPresentation } from "../../ruleIntegrationTypes";
import type { RuleIntegrationComparison } from "../../ruleIntegrationTypes";
import type { CreateRuleTableIntegrationAdapterParams } from "../../executableFamilies/ruleTable/RuleTableIntegrationAdapter";
import { requireRulePolicyBinding } from "../rulePolicyBindingRegistry";
import {
    bindRuleTableCellExpression,
    normalizeRuleTableCellExpressions,
} from "./ruleTableExpressionBinding";

export const WF009_RULE_ID = "WF-009";
export const WF010_RULE_ID = "WF-010";
export const WF011_RULE_ID = "WF-011";
export const WF012_RULE_ID = "WF-012";
export const CF014_RULE_ID = "CF-014";

const CATALOG_DECISION_APPGLUE_EXECUTABLE_TYPE = "RuleTableConfiguration";

type CatalogDecisionFieldType = "string" | "number" | "boolean";

type CatalogDecisionField = {
    name: string;
    type: CatalogDecisionFieldType;
};

type CatalogDecisionCondition =
    | {
          field: string;
          operator: "==";
          value: string | boolean;
      }
    | {
          field: string;
          operator: "in";
          value: string[];
      }
    | {
          field: string;
          operator: ">" | ">=" | "<" | "<=";
          value: number;
      };

type CatalogDecisionResult = {
    outcome: string;
    status: Exclude<DemoRuleStatus, "skipped">;
    output_queue?: DemoOutputQueue;
    reason_code: string;
    explanation: string;
    action: string;
};

type CatalogDecisionRow = {
    id: string;
    label: string;
    conditions: CatalogDecisionCondition[];
    result: CatalogDecisionResult;
};

export type CatalogDecisionRuleTableRuntimeRow = Record<string, string | number | boolean | null | undefined>;

type CatalogDecisionRuleTablePolicySpec = {
    ruleId: string;
    title: string;
    fields: CatalogDecisionField[];
    rows: CatalogDecisionRow[];
    defaultResult: CatalogDecisionResult;
    summaryLookupLabel: string;
    comparisonLabels?: Record<string, string>;
    runtimeSamples: {
        passingRow: CatalogDecisionRuleTableRuntimeRow;
        routedRow: CatalogDecisionRuleTableRuntimeRow;
    };
};

type CatalogDecisionRuleTableResult = CatalogDecisionResult & {
    matched_row?: string;
};

type CatalogDecisionRuleTableRowSummary = {
    rowId: string;
    matched_row?: string;
    conditions: string[];
    result: CatalogDecisionRuleTableResult;
};

const RESULT_COLUMN_NAMES = [
    "outcome",
    "status",
    "output_queue",
    "reason_code",
    "explanation",
    "action",
    "matched_row",
] as const;

const OUTPUT_QUEUES: DemoOutputQueue[] = [
    "published_catalog_candidates",
    "quarantine_records",
    "archived_parts",
    "pending_review",
    "procurement_review_queue",
    "compliance_review_queue",
    "salesforce_product_create_events",
    "salesforce_price_alert_events",
    "salesforce_obsolescence_alert_events",
    "epicor_cost_file_updates",
    "epicor_description_previews",
    "salesforce_product_name_previews",
    "source_override_audit",
];

const isOutputQueue = (value: unknown): value is DemoOutputQueue =>
    typeof value === "string" && OUTPUT_QUEUES.includes(value as DemoOutputQueue);

const isStatus = (value: unknown): value is Exclude<DemoRuleStatus, "skipped"> =>
    value === "pass" || value === "flagged" || value === "rejected";

const asString = (value: unknown, fallback: string): string => (typeof value === "string" ? value : fallback);

const createFieldDefinition = (field: CatalogDecisionField) => {
    if (field.type === "number") return new NumberDataDefinition(field.name);
    if (field.type === "boolean") return new BooleanDataDefinition(field.name);
    return new StringDataDefinition(field.name);
};

const createInputDefinition = (fields: CatalogDecisionField[]): DataDefinition => {
    const inputData = new DataDefinition();
    fields.forEach((field) => inputData.fields.push(createFieldDefinition(field)));
    return inputData;
};

const createResultDefinition = (): DataDefinition => {
    const resultData = new DataDefinition();
    RESULT_COLUMN_NAMES.forEach((column) => resultData.fields.push(new StringDataDefinition(column)));
    return resultData;
};

const buildExecutionData = (row: CatalogDecisionRuleTableRuntimeRow): ExecutionData => new ExecutionData(row);

const expressionConfig = (expression: ExpressionRule): ExpressionConfiguration => {
    const config = new ExpressionConfiguration();
    config.expression = expression;
    return config;
};

const trueExpressionConfig = (): ExpressionConfiguration => {
    const config = new ExpressionConfiguration();
    config.allowTrueValueResult = true;
    config.value = true;
    return config;
};

const stringEquals = (field: string, value: string): StringEqualExpression => {
    const expression = new StringEqualExpression();
    expression.value1.variableName = field;
    expression.value2.value = value;
    return expression;
};

const stringIn = (field: string, values: string[]): StringListInExpression => {
    const expression = new StringListInExpression();
    expression.value1.variableName = field;
    expression.value2.value = values;
    return expression;
};

const booleanEquals = (field: string, value: boolean): IsTrueExpression | IsFalseExpression => {
    const expression = value ? new IsTrueExpression() : new IsFalseExpression();
    expression.value1.variableName = field;
    return expression;
};

const numberComparison = (condition: Extract<CatalogDecisionCondition, { value: number }>): ExpressionRule => {
    const expression = (() => {
        if (condition.operator === ">") return new GreaterThanExpression();
        if (condition.operator === ">=") return new GreaterThanEqualToExpression();
        if (condition.operator === "<") return new LessThanExpression();
        return new LessThanEqualToExpression();
    })();

    expression.value1.variableName = condition.field;
    expression.value2.value = condition.value;
    return expression;
};

const expressionForCondition = (condition: CatalogDecisionCondition): ExpressionConfiguration => {
    if (condition.operator === "in") return expressionConfig(stringIn(condition.field, condition.value));
    if (condition.operator === "==" && typeof condition.value === "boolean") {
        return expressionConfig(booleanEquals(condition.field, condition.value));
    }
    if (condition.operator === "==" && typeof condition.value === "string") {
        return expressionConfig(stringEquals(condition.field, condition.value));
    }
    if (
        condition.operator === ">" ||
        condition.operator === ">=" ||
        condition.operator === "<" ||
        condition.operator === "<="
    ) {
        return expressionConfig(numberComparison(condition));
    }
    throw new Error(`Unsupported catalog decision condition for ${condition.field}.`);
};

const setRuleColumnExpression = (
    config: RuleTableConfiguration,
    row: RuleTableRow,
    column: RuleTableColumn,
    condition: CatalogDecisionCondition | undefined,
) => {
    bindRuleTableCellExpression(
        config,
        row,
        column,
        condition ? expressionForCondition(condition) : trueExpressionConfig(),
    );
};

const getResultColumnId = (
    config: RuleTableConfiguration,
    name: (typeof RESULT_COLUMN_NAMES)[number],
): string => {
    const column = config.resultColumns.find((candidate) => candidate.name === name);
    if (!column) throw new Error(`Catalog decision rule table is missing result column ${name}.`);
    return column._id;
};

const setResultCellValue = (
    config: RuleTableConfiguration,
    row: RuleTableRow,
    name: (typeof RESULT_COLUMN_NAMES)[number],
    value: string,
) => {
    row.resultCellsData[getResultColumnId(config, name)].value = value;
};

const getResultCellValue = (
    config: RuleTableConfiguration,
    row: RuleTableRow,
    name: (typeof RESULT_COLUMN_NAMES)[number],
): unknown => {
    const column = config.resultColumns.find((candidate) => candidate.name === name);
    if (!column) return undefined;
    return row.resultCellsData[column._id]?.value;
};

const setDefaultResult = (config: RuleTableConfiguration, result: CatalogDecisionResult) => {
    RESULT_COLUMN_NAMES.forEach((name) => {
        config.defaultResult[getResultColumnId(config, name)].value =
            name === "matched_row" ? "default" : normalizeResultString(result, name);
    });
};

const normalizeResultString = (
    result: CatalogDecisionResult,
    name: (typeof RESULT_COLUMN_NAMES)[number],
): string => {
    if (name === "matched_row") return "";
    if (name === "output_queue") return result.output_queue ?? "";
    if (name === "reason_code") return result.reason_code;
    return String(result[name]);
};

const setRowResult = (
    config: RuleTableConfiguration,
    row: RuleTableRow,
    decisionRow: CatalogDecisionRow,
) => {
    RESULT_COLUMN_NAMES.forEach((name) => {
        setResultCellValue(
            config,
            row,
            name,
            name === "matched_row" ? decisionRow.id : normalizeResultString(decisionRow.result, name),
        );
    });
};

const createCatalogDecisionRuleTableConfiguration = (
    spec: CatalogDecisionRuleTablePolicySpec,
): RuleTableConfiguration => {
    AppGlueCoreInit.init();

    const config = new RuleTableConfiguration();
    config.title = spec.title;
    config.inputData = createInputDefinition(spec.fields);
    config.executionResult = createResultDefinition();

    const columns = spec.fields.map((field, index) => {
        const column = index === 0 ? config.ruleColumns[0]! : config.addRuleColumn(field.name);
        config.setRuleColumn(column, field.name);
        return column;
    });

    while (config.rows.length > 0) {
        config.removeRow(config.rows[0]!);
    }

    spec.rows.forEach((decisionRow) => {
        const row = config.addRow();
        columns.forEach((column) => {
            setRuleColumnExpression(
                config,
                row,
                column,
                decisionRow.conditions.find((condition) => condition.field === column.name),
            );
        });
        setRowResult(config, row, decisionRow);
    });

    setDefaultResult(config, spec.defaultResult);
    normalizeRuleTableCellExpressions(config);
    return config;
};

const normalizeRuleTableResult = (result: unknown): CatalogDecisionRuleTableResult => {
    const data = result && typeof result === "object" ? (result as Record<string, unknown>) : {};
    const status = isStatus(data.status) ? data.status : "rejected";
    const outputQueue = isOutputQueue(data.output_queue) ? data.output_queue : undefined;

    return {
        outcome: asString(data.outcome, status),
        status,
        output_queue: outputQueue,
        reason_code: asString(data.reason_code, status === "pass" ? "PASS" : "RULE_TABLE_ROUTE"),
        explanation: asString(data.explanation, "Catalog decision rule table produced a routed result."),
        action: asString(data.action, status === "pass" ? "allow downstream processing" : "route for review"),
        matched_row: asString(data.matched_row, "default"),
    };
};

const getRuleTableRows = (config: RuleTableConfiguration): CatalogDecisionRuleTableRowSummary[] =>
    config.rows.map((row) => ({
        rowId: row._id,
        matched_row: asString(getResultCellValue(config, row, "matched_row"), ""),
        conditions: config.ruleColumns.flatMap((column) => {
            const expression = row.ruleCells[column._id]?.expression?.expression;
            if (expression instanceof IsTrueExpression) return [`${column.name} == true`];
            if (expression instanceof IsFalseExpression) return [`${column.name} == false`];
            if (expression instanceof StringEqualExpression) {
                return [`${column.name} == ${String(expression.value2.value)}`];
            }
            if (expression instanceof StringListInExpression) {
                const values = Array.isArray(expression.value2.value) ? expression.value2.value.join(", ") : "";
                return [`${column.name} in ${values}`];
            }
            if (expression instanceof GreaterThanExpression) return [`${column.name} > ${expression.value2.value}`];
            if (expression instanceof GreaterThanEqualToExpression) {
                return [`${column.name} >= ${expression.value2.value}`];
            }
            if (expression instanceof LessThanExpression) return [`${column.name} < ${expression.value2.value}`];
            if (expression instanceof LessThanEqualToExpression) {
                return [`${column.name} <= ${expression.value2.value}`];
            }
            return [];
        }),
        result: normalizeRuleTableResult(
            Object.fromEntries(RESULT_COLUMN_NAMES.map((name) => [name, getResultCellValue(config, row, name)])),
        ),
    }));

const renderSummary = (
    config: RuleTableConfiguration,
    spec: CatalogDecisionRuleTablePolicySpec,
) => {
    const rows = getRuleTableRows(config);
    const sampleRows = rows
        .slice(0, 3)
        .map((row) => `${row.conditions.join(" AND ") || "default"} -> ${row.result.status}`)
        .join("; ");
    const routes = Array.from(
        new Set(
            rows
                .map((row) =>
                    row.result.output_queue ? `${row.result.status} -> ${row.result.output_queue}` : row.result.status,
                )
                .filter(Boolean),
        ),
    );

    return createRuleSummaryPresentation({
        status: "complete",
        lines: [
            `LOOKUP ${rows.length} ${spec.summaryLookupLabel}`,
            sampleRows ? `RULES ${sampleRows}` : "RULES stored in AppGlue rule table",
            `ACTION ${routes.length ? routes.join("; ") : spec.defaultResult.action}`,
        ],
        sources: ["native_appglue_config", "adapter_projection"],
    });
};

const comparisonForField = (
    row: CatalogDecisionRuleTableRuntimeRow,
    field: CatalogDecisionField,
    spec: CatalogDecisionRuleTablePolicySpec,
): RuleIntegrationComparison => ({
    label: spec.comparisonLabels?.[field.name] ?? field.name,
    expected: "evaluated by AppGlue rule table",
    actual: String(row[field.name] ?? "missing"),
});

const createCatalogDecisionRuleTablePolicy = (
    spec: CatalogDecisionRuleTablePolicySpec,
): CreateRuleTableIntegrationAdapterParams<CatalogDecisionRuleTableRuntimeRow> => ({
    ruleId: spec.ruleId,
    policyBinding: requireRulePolicyBinding(spec.ruleId),
    createConfig: () => createCatalogDecisionRuleTableConfiguration(spec),
    evaluator: {
        evaluate: (config, row) => {
            const result = normalizeRuleTableResult(config.run(buildExecutionData(row), undefined));

            return {
                passed: result.status === "pass",
                status: result.status,
                reasonCode: result.reason_code,
                explanation: result.explanation,
                outputQueue: result.output_queue,
                comparisons: [
                    {
                        label: "Rule table outcome",
                        expected: "pass",
                        actual: result.status,
                        ok: result.status === "pass",
                    },
                    ...spec.fields.map((field) => comparisonForField(row, field, spec)),
                ],
                runtimeEvidence: {
                    runtime: "appglue_runtime",
                    executableType: CATALOG_DECISION_APPGLUE_EXECUTABLE_TYPE,
                },
                evidence: {
                    ...result,
                    inputs: row,
                },
            };
        },
    },
    summaryPresenter: {
        renderSummary: (config) => renderSummary(config, spec),
    },
});

const passResult = (reasonCode: string, explanation: string): CatalogDecisionResult => ({
    outcome: "pass",
    status: "pass",
    reason_code: reasonCode,
    explanation,
    action: "allow downstream processing",
});

const WF009_SPEC: CatalogDecisionRuleTablePolicySpec = {
    ruleId: WF009_RULE_ID,
    title: "WF-009 new part introduction workflow table",
    fields: [{ name: "has_catalog_record", type: "boolean" }],
    summaryLookupLabel: "catalog-existence workflow rows",
    comparisonLabels: { has_catalog_record: "Catalog lookup" },
    rows: [
        {
            id: "net-new-part",
            label: "Net-new part",
            conditions: [{ field: "has_catalog_record", operator: "==", value: false }],
            result: {
                outcome: "net_new_part",
                status: "flagged",
                output_queue: "salesforce_product_create_events",
                reason_code: "NET_NEW_PART",
                explanation: "No prior catalog record exists, so the row creates a product-review event.",
                action: "create CRM product-review event",
            },
        },
    ],
    defaultResult: passResult("CATALOG_RECORD_EXISTS", "Existing catalog record found, so no new product workflow is needed."),
    runtimeSamples: {
        passingRow: { has_catalog_record: true },
        routedRow: { has_catalog_record: false },
    },
};

const WF010_SPEC: CatalogDecisionRuleTablePolicySpec = {
    ruleId: WF010_RULE_ID,
    title: "WF-010 price increase notification workflow table",
    fields: [
        { name: "price_change_pct", type: "number" },
        { name: "has_open_opportunity", type: "boolean" },
    ],
    summaryLookupLabel: "price-alert workflow rows",
    comparisonLabels: {
        price_change_pct: "Material increase",
        has_open_opportunity: "Open opportunity",
    },
    rows: [
        {
            id: "open-opportunity-margin-risk",
            label: "Open opportunity margin risk",
            conditions: [
                { field: "price_change_pct", operator: ">", value: 15 },
                { field: "has_open_opportunity", operator: "==", value: true },
            ],
            result: {
                outcome: "open_opportunity_margin_risk",
                status: "flagged",
                output_queue: "salesforce_price_alert_events",
                reason_code: "OPEN_OPPORTUNITY_MARGIN_RISK",
                explanation: "A material price increase affects open commercial activity and should alert the account owner.",
                action: "create CRM margin-risk alert",
            },
        },
    ],
    defaultResult: passResult(
        "NO_PRICE_ALERT",
        "No open opportunity needs a material price increase alert for this update.",
    ),
    runtimeSamples: {
        passingRow: { price_change_pct: 4, has_open_opportunity: true },
        routedRow: { price_change_pct: 25, has_open_opportunity: true },
    },
};

const WF011_SPEC: CatalogDecisionRuleTablePolicySpec = {
    ruleId: WF011_RULE_ID,
    title: "WF-011 obsolescence cross-sell workflow table",
    fields: [
        { name: "lifecycle_status", type: "string" },
        { name: "has_open_activity", type: "boolean" },
    ],
    summaryLookupLabel: "obsolescence workflow rows",
    comparisonLabels: {
        lifecycle_status: "Lifecycle",
        has_open_activity: "Open activity",
    },
    rows: [
        {
            id: "obsolete-with-open-activity",
            label: "Obsolete with open activity",
            conditions: [
                { field: "lifecycle_status", operator: "in", value: ["EOL", "NRND"] },
                { field: "has_open_activity", operator: "==", value: true },
            ],
            result: {
                outcome: "obsolescence_with_open_activity",
                status: "flagged",
                output_queue: "salesforce_obsolescence_alert_events",
                reason_code: "OBSOLESCENCE_WITH_OPEN_ACTIVITY",
                explanation:
                    "The obsolete or not-recommended part appears on open commercial activity and needs an alternate-part alert.",
                action: "alert with replacement part when available",
            },
        },
        {
            id: "obsolete-archive-only",
            label: "Obsolete archive only",
            conditions: [
                { field: "lifecycle_status", operator: "in", value: ["EOL", "NRND"] },
                { field: "has_open_activity", operator: "==", value: false },
            ],
            result: {
                outcome: "obsolescence_archive_only",
                status: "rejected",
                output_queue: "archived_parts",
                reason_code: "OBSOLESCENCE_ARCHIVE_ONLY",
                explanation: "The part is obsolete or not recommended, but no open opportunity or purchase order is attached.",
                action: "archive part without CRM alert",
            },
        },
    ],
    defaultResult: passResult("NO_OBSOLESCENCE_ALERT", "Lifecycle status does not require an obsolescence cross-sell alert."),
    runtimeSamples: {
        passingRow: { lifecycle_status: "Active", has_open_activity: false },
        routedRow: { lifecycle_status: "EOL", has_open_activity: true },
    },
};

const WF012_SPEC: CatalogDecisionRuleTablePolicySpec = {
    ruleId: WF012_RULE_ID,
    title: "WF-012 compliance change workflow table",
    fields: [{ name: "has_compliance_change", type: "boolean" }],
    summaryLookupLabel: "compliance-change workflow rows",
    comparisonLabels: { has_compliance_change: "RoHS change" },
    rows: [
        {
            id: "compliance-changed",
            label: "Compliance changed",
            conditions: [{ field: "has_compliance_change", operator: "==", value: true }],
            result: {
                outcome: "compliance_changed",
                status: "flagged",
                output_queue: "compliance_review_queue",
                reason_code: "COMPLIANCE_CHANGED",
                explanation: "Compliance changed from the prior catalog value and must be reviewed before overwrite.",
                action: "hold compliance overwrite for review",
            },
        },
    ],
    defaultResult: passResult("COMPLIANCE_STABLE", "Compliance status is stable against the prior catalog value."),
    runtimeSamples: {
        passingRow: { has_compliance_change: false },
        routedRow: { has_compliance_change: true },
    },
};

const sourcePriorityRows = (rules: SourcePriorityRule[]): CatalogDecisionRow[] =>
    rules.map((rule) => ({
        id: `source-priority-${rule.attributeName}`,
        label: `${rule.attributeName} source priority`,
        conditions: [
            { field: "attribute_name", operator: "==", value: rule.attributeName },
            { field: "has_source_conflict", operator: "==", value: true },
        ],
        result: {
            outcome: "source_priority_override",
            status: "flagged",
            output_queue: "source_override_audit",
            reason_code: "SOURCE_PRIORITY_OVERRIDE",
            explanation: `${rule.defaultAction}. Conflicting attribute sources were resolved by source hierarchy and logged for audit.`,
            action: "log source override decisions",
        },
    }));

const createCf014Spec = (
    sourcePriorityRules: SourcePriorityRule[] = SOURCE_PRIORITY_RULES,
): CatalogDecisionRuleTablePolicySpec => ({
    ruleId: CF014_RULE_ID,
    title: "CF-014 attribute source hierarchy rule table",
    fields: [
        { name: "attribute_name", type: "string" },
        { name: "has_source_conflict", type: "boolean" },
    ],
    summaryLookupLabel: "source-priority rows",
    comparisonLabels: {
        attribute_name: "Attribute",
        has_source_conflict: "Source hierarchy",
    },
    rows: sourcePriorityRows(sourcePriorityRules),
    defaultResult: passResult("SOURCE_PRIORITY_CLEAR", "No source hierarchy conflict is present for this row."),
    runtimeSamples: {
        passingRow: { attribute_name: "packageType", has_source_conflict: false },
        routedRow: { attribute_name: "rohsStatus", has_source_conflict: true },
    },
});

export const WF009_RULE_TABLE_POLICY = createCatalogDecisionRuleTablePolicy(WF009_SPEC);
export const WF010_RULE_TABLE_POLICY = createCatalogDecisionRuleTablePolicy(WF010_SPEC);
export const WF011_RULE_TABLE_POLICY = createCatalogDecisionRuleTablePolicy(WF011_SPEC);
export const WF012_RULE_TABLE_POLICY = createCatalogDecisionRuleTablePolicy(WF012_SPEC);

export const CF014_RULE_TABLE_POLICY: CreateRuleTableIntegrationAdapterParams<CatalogDecisionRuleTableRuntimeRow> = {
    ...createCatalogDecisionRuleTablePolicy(createCf014Spec()),
    createConfig: (context = {}) =>
        createCatalogDecisionRuleTableConfiguration(createCf014Spec(context.data?.sourcePriorityRules)),
};

export const WF009_RUNTIME_SAMPLES = WF009_SPEC.runtimeSamples;
export const WF010_RUNTIME_SAMPLES = WF010_SPEC.runtimeSamples;
export const WF011_RUNTIME_SAMPLES = WF011_SPEC.runtimeSamples;
export const WF012_RUNTIME_SAMPLES = WF012_SPEC.runtimeSamples;
export const CF014_RUNTIME_SAMPLES = createCf014Spec().runtimeSamples;
